import { useState } from 'react'
import { Button } from '@/components/ui'
import { SEO, PricingSchema } from '@/components/seo'
import { GradientHero } from '@/components/layout'
import { PlanComparison, ContactSalesModal } from '@/components/pricing'
import { usePricingTiers } from '@/hooks/usePricingTiers'
import { useUtmPassthrough } from '@/hooks/useUtmPassthrough'
import { defaultFaqs, entryMonthlyPrice } from '@/data/pricing'
import { appSignupLink } from '@/utils/signupUrl'
import { trackSignupCtaClick } from '@/utils/rum'
import type { PricingTier } from '@/types/pricing'

function CheckIcon() {
  return (
    <svg
      className="w-[18px] h-[18px] text-accent-teal flex-shrink-0 mt-0.5"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  )
}

function priceFor(tier: PricingTier, annual: boolean): string {
  if (tier.monthlyPrice === null) return 'Custom'
  const value = annual && tier.annualPrice !== null ? tier.annualPrice : tier.monthlyPrice
  return `£${value}`
}

export default function PricingPageV2() {
  const [annual, setAnnual] = useState(false)
  const [contactSalesPlan, setContactSalesPlan] = useState<string | null>(null)
  const { tiers } = usePricingTiers()
  const utm = useUtmPassthrough()

  return (
    <>
      <SEO
        title="WealthSpace Pricing | Modular AI software for UK advisers"
        description={
          entryMonthlyPrice > 0
            ? `Modular pricing from £${entryMonthlyPrice}/adviser/month. Card-secured 7-day trial · Cancel anytime. AI reports, client management, and compliance, all built around your firm.`
            : 'Modular per-adviser pricing. Card-secured 7-day trial · Cancel anytime. AI reports, client management, and compliance, all built around your firm.'
        }
        keywords="financial adviser software pricing, IFA software cost UK, modular pricing, AI adviser tools cost, paraplanning software pricing, wealth management platform cost"
        url="/pricing"
      />
      <PricingSchema />

      <GradientHero
        revisionBadge="New pricing: April 2026"
        title={
          <>
            Simple, <span className="italic text-teal-200">modular</span> pricing
          </>
        }
        subtitle={
          <>
            Start with the modules you need today. Add more as you grow. Card-secured 7-day trial.
          </>
        }
        meta="No setup fees on standard plans · Cancel anytime · UK-based support"
        actions={
          <div className="flex items-center justify-center gap-4">
            <span
              className={`text-sm transition-colors duration-300 ${annual ? 'text-white/50' : 'text-white'}`}
            >
              Monthly
            </span>
            <button
              type="button"
              onClick={() => setAnnual((v) => !v)}
              aria-pressed={annual}
              aria-label="Toggle annual billing"
              className={`w-11 h-6 rounded-xl relative cursor-pointer transition-colors duration-300 ${
                annual ? 'bg-accent-tealLight' : 'bg-white/30'
              }`}
            >
              <span
                className={`absolute top-[3px] left-[3px] w-[18px] h-[18px] bg-white rounded-full transition-transform duration-300 ${
                  annual ? 'translate-x-5' : ''
                }`}
              />
            </button>
            <span
              className={`text-sm transition-colors duration-300 ${annual ? 'text-white' : 'text-white/50'}`}
            >
              Annual <span className="text-teal-200 font-medium">(Save up to 10%)</span>
            </span>
          </div>
        }
      />

      <section className="px-6 pt-16 pb-20 max-w-[1200px] mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {tiers.map((tier) => {
            const link = appSignupLink({ plan: tier.slug, params: utm })
            return (
              <div
                key={tier.slug}
                id={tier.slug}
                className={`bg-white border rounded-2xl py-10 px-8 relative transition-all duration-400 hover:-translate-y-1 hover:shadow-lg ${
                  tier.popular ? 'border-accent-teal shadow-md' : 'border-slate-200 shadow-card'
                }`}
              >
                {tier.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-accent-teal text-white text-xs font-semibold px-4 py-1.5 rounded-full whitespace-nowrap">
                    Most Popular
                  </div>
                )}

                <h3 className="text-2xl font-semibold text-slate-900 mb-2">{tier.displayName}</h3>
                <p className="text-sm text-slate-500 mb-6">{tier.description}</p>

                <div className="mb-8">
                  {tier.monthlyPrice !== null ? (
                    <>
                      <span className="font-serif text-5xl text-slate-900">{priceFor(tier, annual)}</span>
                      <span className="text-sm text-slate-500">{tier.priceLabel}</span>
                    </>
                  ) : (
                    <span className="font-serif text-[28px] text-slate-900">Custom pricing</span>
                  )}
                  {tier.audience && (
                    <p className="text-xs text-slate-400 mt-1">{tier.audience}</p>
                  )}
                </div>

                <ul className="mb-8 space-y-3">
                  {tier.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-3 text-sm text-slate-600">
                      <CheckIcon />
                      {feature}
                    </li>
                  ))}
                </ul>

                {tier.isContactSales ? (
                  <Button
                    variant={tier.popular ? 'primary' : 'secondary'}
                    className="w-full justify-center"
                    onClick={() => {
                      trackSignupCtaClick({
                        location: 'pricing_card',
                        plan: tier.slug,
                        hasUtm: Object.keys(utm).length > 0,
                      })
                      setContactSalesPlan(tier.displayName)
                    }}
                  >
                    {tier.ctaLabel}
                  </Button>
                ) : (
                  <Button
                    href={link.href}
                    external={link.external}
                    variant={tier.popular ? 'primary' : 'secondary'}
                    className="w-full justify-center"
                    onClick={() =>
                      trackSignupCtaClick({
                        location: 'pricing_card',
                        plan: tier.slug,
                        hasUtm: Object.keys(utm).length > 0,
                      })
                    }
                  >
                    {tier.ctaLabel}
                  </Button>
                )}
                {!tier.isContactSales && (
                  <p className="text-xs text-slate-400 mt-2 text-center">
                    Card-secured 7-day trial · Cancel anytime
                  </p>
                )}
              </div>
            )
          })}
        </div>

        <p className="text-center text-xs text-slate-400 mt-6">All prices exclude VAT.</p>

        {/* Legacy reassurance */}
        <div className="max-w-[700px] mx-auto mt-16 bg-teal-50 border border-teal-200 rounded-xl p-6 text-center">
          <p className="text-sm font-semibold text-accent-teal uppercase tracking-wide mb-2">
            Already a customer?
          </p>
          <p className="text-slate-700 mb-3">
            Your plan, price, and feature access are <strong>unchanged</strong>. Existing customers stay on their current plan as long as they want to.
          </p>
          <a
            href="/pricing-update"
            className="text-accent-teal text-sm font-semibold hover:underline"
          >
            Read the FAQ →
          </a>
        </div>
      </section>

      <PlanComparison tiers={tiers} />

      {/* FAQs */}
      <section className="py-20 px-6 bg-slate-50">
        <div className="max-w-[800px] mx-auto">
          <h2 className="font-serif text-4xl font-normal text-slate-900 text-center mb-12">
            Frequently asked <span className="text-accent-teal italic">questions</span>
          </h2>

          <div className="space-y-4">
            {defaultFaqs.map((faq) => (
              <div
                key={faq.question}
                className="bg-white border border-slate-200 rounded-2xl p-6 shadow-card"
              >
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{faq.question}</h3>
                <p className="text-[15px] text-slate-600 leading-relaxed">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <ContactSalesModal
        isOpen={contactSalesPlan !== null}
        onClose={() => setContactSalesPlan(null)}
        planName={contactSalesPlan ?? 'Enterprise'}
      />
    </>
  )
}
